import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Button, IconButton, InlineLoading } from "@carbon/react";
import { Add, TrashCan } from "@carbon/icons-react";
import Form from "../../../components/carbon/Form/Form";
import SelectField from "../../../components/carbon/Form/SelectField";
import TextField from "../../../components/carbon/Form/TextField";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import PageHeader from "../../../components/carbon/PageHeader/PageHeader";
import { useNotification } from "../../../context";
import {
  useApprovalRules,
  useReplaceApprovalRules,
} from "../../../hooks/useApprovalRules";
import { useFamilyOptions } from "../../../hooks/useCategories";
import { useUsersByCompany } from "../../../hooks/useUsers";
import { ApprovalRule, ApprovalRuleInput } from "../../../types";
import "./approvalRules.scss";

/** One editable tier. `minAmount` stays a string while typing; `""` approver = "any approver". */
interface TierFields {
  currency: string;
  minAmount: string;
  label: string;
  approverUserId: string;
  /** `""` = company-wide ("all families"). */
  materialCategoryId: string;
}

interface ApprovalRulesFormFields {
  rules: TierFields[];
}

const emptyTier: TierFields = {
  currency: "",
  minAmount: "",
  label: "",
  approverUserId: "",
  materialCategoryId: "",
};

/** Currency (A-Z), then minimum amount: the order the backend returns, re-applied defensively. */
const byCurrencyThenAmount = (a: ApprovalRule, b: ApprovalRule) =>
  a.currency.localeCompare(b.currency) || a.minAmount - b.minAmount;

const toFormFields = (rules: ApprovalRule[]): ApprovalRulesFormFields => ({
  rules: [...rules].sort(byCurrencyThenAmount).map((rule) => ({
    currency: rule.currency,
    minAmount: String(rule.minAmount),
    label: rule.label ?? "",
    approverUserId: rule.approverUserId ?? "",
    materialCategoryId: rule.materialCategoryId ?? "",
  })),
});

/**
 * The PUT is REPLACE: this is the FULL desired set (an omitted tier is deleted server-side,
 * `[]` clears everything). A blank approver is `null` ("any holder of APPROVE"), a blank label is
 * `null`; a blank family is `null` (the tier applies company-wide).
 */
const toRequest = (values: ApprovalRulesFormFields): ApprovalRuleInput[] =>
  values.rules.map((rule) => ({
    currency: rule.currency.trim().toUpperCase(),
    minAmount: Number(rule.minAmount),
    approverUserId: rule.approverUserId || null,
    label: rule.label.trim() || null,
    materialCategoryId: rule.materialCategoryId || null,
  }));

const buildSchema = (t: (key: string) => string) =>
  Yup.object({
    rules: Yup.array()
      .of(
        Yup.object({
          currency: Yup.string()
            .trim()
            .required(t("purchaseOrders.approvalRules.validation.currency")),
          minAmount: Yup.string()
            .required(t("purchaseOrders.approvalRules.validation.minAmount"))
            .test(
              "min-amount",
              t("purchaseOrders.approvalRules.validation.minAmount"),
              (value?: string) =>
                value !== undefined &&
                value.trim() !== "" &&
                Number.isFinite(Number(value)) &&
                Number(value) >= 0
            ),
          label: Yup.string().default(""),
          approverUserId: Yup.string().default(""),
          materialCategoryId: Yup.string().default(""),
        })
      )
      .default([]),
  });

/**
 * Approval rules (per-currency tiers) of the company's purchase orders. Editing is local: add /
 * edit / remove tiers, then "Save" PUTs the FULL set in one request (REPLACE semantics — see
 * `toRequest`). Save stays disabled until the set differs from what was loaded/last saved. A 400
 * (duplicate currency+amount, or an approver outside the company) is an error toast, never a crash.
 *
 * The family (material category) is optional too: blank = "Todas las familias", the tier applies
 * company-wide (`materialCategoryId: null`).
 *
 * The approver is optional: blank = any user who holds APPROVE. The options are the company's
 * users; an already-saved approver who is not in that list (deactivated, still loading) is kept
 * as an "unknown user" option so a round trip never silently drops it.
 */
const ApprovalRules = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const { data: rules, isLoading, isError } = useApprovalRules();
  const replace = useReplaceApprovalRules();
  const { data: users } = useUsersByCompany("");
  const families = useFamilyOptions();

  const defaults = useMemo(() => toFormFields(rules ?? []), [rules]);
  const schema = useMemo(() => buildSchema(t), [t]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<ApprovalRulesFormFields>({
    resolver: yupResolver(schema) as never,
    defaultValues: defaults,
  });

  // A (re)fetched rule set is the new baseline.
  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  const { fields, append, remove } = useFieldArray({ control, name: "rules" });

  const approverOptions = (index: number) => {
    const options = (users ?? []).map((u) => ({
      value: u.id,
      label: u.fullName || u.email,
    }));
    const current = watch(`rules.${index}.approverUserId`);
    if (current && !options.some((o) => o.value === current)) {
      options.push({
        value: current,
        label: t("purchaseOrders.approvalRules.unknownApprover", {
          id: current,
        }),
      });
    }
    return options;
  };

  const familyOptions = (index: number) => {
    const options = [...families];
    const current = watch(`rules.${index}.materialCategoryId`);
    if (current && !options.some((o) => o.value === current)) {
      options.push({
        value: current,
        label: t("purchaseOrders.approvalRules.unknownFamily", { id: current }),
      });
    }
    return options;
  };

  const onSubmit = (values: ApprovalRulesFormFields) =>
    replace.mutate(toRequest(values), {
      onSuccess: () => {
        // Re-baseline right away (the refetch that follows lands the server-assigned ids).
        reset(values);
        openNotification(t("purchaseOrders.approvalRules.success"));
      },
      onError: (error) =>
        openNotification(
          t(
            (error as { response?: { status?: number } } | null)?.response
              ?.status === 400
              ? "purchaseOrders.approvalRules.invalid"
              : "purchaseOrders.approvalRules.error"
          )
        ),
    });

  return (
    <div className="approval-rules">
      <PageHeader
        title={t("purchaseOrders.approvalRules.title")}
        action={
          <Button
            kind="tertiary"
            size="md"
            renderIcon={Add}
            type="button"
            onClick={() => append(emptyTier)}
          >
            {t("purchaseOrders.approvalRules.addTier")}
          </Button>
        }
      />
      <p className="approval-rules__description">
        {t("purchaseOrders.approvalRules.description")}
      </p>

      {isLoading ? (
        <InlineLoading description={t("cta.loading")} />
      ) : isError ? (
        <p role="alert">{t("purchaseOrders.approvalRules.loadError")}</p>
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          noValidate
          className="approval-rules__form"
        >
          {fields.length === 0 ? (
            <EmptyState title={t("purchaseOrders.approvalRules.empty")} />
          ) : (
            <div className="approval-rules__rows">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="approval-rules__row"
                  role="group"
                  aria-label={t("purchaseOrders.approvalRules.tier", {
                    index: index + 1,
                  })}
                >
                  <div className="approval-rules__grid">
                    <TextField
                      name={`rules.${index}.currency`}
                      label={t("purchaseOrders.approvalRules.currency")}
                      control={control}
                      maxLength={10}
                    />
                    <TextField
                      name={`rules.${index}.minAmount`}
                      label={t("purchaseOrders.approvalRules.minAmount")}
                      control={control}
                      type="number"
                      min={0}
                    />
                    <TextField
                      name={`rules.${index}.label`}
                      label={t("purchaseOrders.approvalRules.label")}
                      optional
                      control={control}
                    />
                    <SelectField
                      name={`rules.${index}.approverUserId`}
                      label={t("purchaseOrders.approvalRules.approver")}
                      optional
                      control={control}
                      placeholder={t(
                        "purchaseOrders.approvalRules.anyApprover"
                      )}
                      options={approverOptions(index)}
                    />
                    <SelectField
                      name={`rules.${index}.materialCategoryId`}
                      label={t("purchaseOrders.approvalRules.family")}
                      optional
                      control={control}
                      placeholder={t(
                        "purchaseOrders.approvalRules.allFamilies"
                      )}
                      options={familyOptions(index)}
                    />
                  </div>
                  <IconButton
                    kind="ghost"
                    size="md"
                    label={t("purchaseOrders.approvalRules.removeTier", {
                      index: index + 1,
                    })}
                    onClick={() => remove(index)}
                  >
                    <TrashCan />
                  </IconButton>
                </div>
              ))}
            </div>
          )}

          <div className="approval-rules__actions">
            <Button type="submit" disabled={replace.isPending || !isDirty}>
              {t("purchaseOrders.approvalRules.save")}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default ApprovalRules;
